import React, { useEffect, useMemo, useState } from 'react';
import { Holding, Currency } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { convertCurrency, formatCurrency } from '../utils/currency';
import { TrendingUp, CheckSquare, Square } from 'lucide-react';
const COLORS = ['#2563eb', '#ea580c', '#16a34a', '#9333ea', '#db2777', '#0891b2', '#ca8a04', '#4f46e5', '#e11d48', '#059669' // emerald-600
];
interface AssetGrowthChartProps {
  holdings: Holding[];
  baseCurrency: Currency;
  timeHorizon: number;
}
export function AssetGrowthChart({
  holdings,
  baseCurrency,
  timeHorizon
}: AssetGrowthChartProps) {
  const activeHoldings = useMemo(() => holdings.filter(h => h.isActive ?? true), [holdings]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Initialize all active holdings as selected when holdings change
  useEffect(() => {
    setSelectedIds(new Set(activeHoldings.map(h => h.id)));
  }, [activeHoldings]);
  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (selectedIds.size === activeHoldings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeHoldings.map(h => h.id)));
    }
  };
  const chartData = useMemo(() => {
    const data = [];
    const currentValues = new Map<string, number>();
    // Initialize starting values
    activeHoldings.forEach(h => {
      currentValues.set(h.id, convertCurrency(h.amount, h.currency, baseCurrency));
    });
    for (let year = 0; year <= timeHorizon; year++) {
      const yearData: any = {
        year,
        label: `Yr ${year}`
      };
      activeHoldings.forEach(h => {
        if (!selectedIds.has(h.id)) return;
        let val = currentValues.get(h.id) || 0;
        yearData[h.id] = Math.round(val);
        // Grow for next year
        const rate = h.isRealEstate ? h.appreciationRate ?? h.expectedReturn ?? 0 : h.expectedReturn ?? 0;
        const cgt = h.capitalGainsTaxRate ?? 0;
        const monthlyRate = rate / 100 / 12;
        const taxMul = 1 - cgt / 100;
        for (let m = 0; m < 12; m++) {
          val += val * monthlyRate * taxMul;
        }
        currentValues.set(h.id, val);
      });
      data.push(yearData);
    }
    return data;
  }, [activeHoldings, baseCurrency, timeHorizon, selectedIds]);
  if (activeHoldings.length === 0) {
    return null;
  }
  return <Card data-id="element-52">
      <CardHeader title="Asset Growth" subtitle="Projected organic growth of individual assets over time (excluding future contributions)" icon={<div className="bg-emerald-600 p-2 rounded-lg shadow-sm" data-id="element-54">
            <TrendingUp className="w-4 h-4 text-white" data-id="element-55" />
          </div>} data-id="element-53" />
      <CardContent className="space-y-6" data-id="element-56">
        {/* Filter Controls */}
        <div className="flex flex-col gap-3" data-id="element-57">
          <div className="flex items-center justify-between" data-id="element-58">
            <span className="text-sm font-medium text-slate-700" data-id="element-59">
              Select Assets to Display
            </span>
            <button onClick={toggleAll} className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1" data-id="element-60">
              {selectedIds.size === activeHoldings.length ? <>
                  <Square className="w-3.5 h-3.5" data-id="element-61" /> Deselect All
                </> : <>
                  <CheckSquare className="w-3.5 h-3.5" data-id="element-62" /> Select All
                </>}
            </button>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-63">
            {activeHoldings.map((h, i) => {
            const isSelected = selectedIds.has(h.id);
            const color = COLORS[i % COLORS.length];
            return <button key={h.id} onClick={() => toggleSelection(h.id)} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all border ${isSelected ? 'bg-slate-50 border-slate-200 text-slate-800 shadow-sm' : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`} data-id="element-64">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: isSelected ? color : '#cbd5e1'
              }} data-id="element-65" />
                  {h.name}
                </button>;
          })}
          </div>
        </div>

        {/* Chart */}
        <div className="h-[400px] mt-4" data-id="element-66">
          {selectedIds.size > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-67">
              <LineChart data={chartData} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-68">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-69" />
                <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
              fontSize: 11,
              fill: '#94a3b8'
            }} dy={8} interval={Math.max(0, Math.floor(chartData.length / 10) - 1)} data-id="element-70" />
                <YAxis axisLine={false} tickLine={false} tick={{
              fontSize: 11,
              fill: '#94a3b8'
            }} tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`} dx={-5} width={55} data-id="element-71" />
                <Tooltip formatter={(value: number, name: string) => {
              const holding = activeHoldings.find(h => h.id === name);
              return [formatCurrency(value, baseCurrency), holding?.name || name];
            }} labelFormatter={(label: string) => `Year ${label}`} contentStyle={{
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 12px rgb(0 0 0 / 0.08)',
              fontSize: '12px',
              padding: '8px 12px'
            }} labelStyle={{
              fontWeight: 600,
              marginBottom: 4
            }} data-id="element-72" />
                {activeHoldings.map((h, i) => {
              if (!selectedIds.has(h.id)) return null;
              return <Line key={h.id} type="monotone" dataKey={h.id} name={h.id} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} activeDot={{
                r: 4,
                strokeWidth: 0
              }} data-id="element-73" />;
            })}
              </LineChart>
            </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg bg-slate-50/50" data-id="element-74">
              Select at least one asset to view its growth trajectory.
            </div>}
        </div>
      </CardContent>
    </Card>;
}